import { cursorPaging, flattenPages } from "@clubcore/api";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useT } from "../../i18n/I18nProvider";
import { Button } from "../../ui/Button";
import { TextField } from "../../ui/Field";
import { EmptyState, ErrorState, Loading } from "../../ui/feedback";
import { Icon } from "../../ui/Icon";
import { LoadMore } from "../../ui/LoadMore";
import { useAuth, useMe } from "../auth";
import { useSelectClub, PRODUCT_NAME } from "../club";
import type { ClubProfile } from "../club-selection";
import { useApi } from "../services";
import { useDocumentTitle } from "../useDocumentTitle";

function ClubOption({ club, onChoose }: { club: { slug: string; name: string }; onChoose: (club: { slug: string }) => void }) {
  return (
    <li>
      <button type="button" className="cc-club-option" onClick={() => onChoose(club)}>
        <span>
          <span className="cc-club-option__name">{club.name}</span>
          <br />
          <span className="cc-club-option__meta">{club.slug}</span>
        </span>
        <Icon name="chevron" size={18} />
      </button>
    </li>
  );
}

/** Platform staff pick from the whole directory (searchable, paged: there can be thousands). */
function StaffDirectory({ onChoose }: { onChoose: (club: { slug: string }) => void }) {
  const t = useT();
  const api = useApi();
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  useEffect(() => {
    const handle = window.setTimeout(() => setTerm(search.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [search]);
  // The server searches name and slug; the list pages in as you scroll.
  const query = useInfiniteQuery({
    queryKey: ["platform", "clubs", term],
    ...cursorPaging(({ cursor, signal }) => api.core.listClubs({ cursor, signal, limit: 50, q: term })),
  });
  const clubs = flattenPages(query.data);

  return (
    <>
      <TextField label={t("club.search")} type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
      {query.isPending ? <Loading /> : query.isError ? <ErrorState error={query.error} onRetry={() => void query.refetch()} /> : clubs.length === 0 ? (
        <EmptyState icon="building" title={t("club.noneFound")} />
      ) : (
        <ul className="cc-club-list">
          {clubs.map((club) => (
            <ClubOption key={club.id} club={club} onChoose={onChoose} />
          ))}
        </ul>
      )}
      <LoadMore hasNextPage={query.hasNextPage} isFetchingNextPage={query.isFetchingNextPage} fetchNextPage={query.fetchNextPage} />
    </>
  );
}

export function ChooseClubPage() {
  const t = useT();
  const me = useMe();
  const { signOut } = useAuth();
  const select = useSelectClub();
  const navigate = useNavigate();
  const from = (useLocation().state as { from?: string } | null)?.from ?? "/";
  useDocumentTitle(t("club.chooseTitle"));

  const choose = (club: { slug: string }) => {
    select(club);
    navigate(from, { replace: true });
  };

  return (
    <div className="cc-center">
      <main className="cc-auth-card cc-auth-card--wide">
        <div className="cc-wordmark">{PRODUCT_NAME}</div>
        <h1>{t("club.chooseTitle")}</h1>
        <p className="cc-tagline">{me.user.is_platform_staff ? t("club.chooseStaff") : t("club.chooseBody")}</p>
        {me.user.is_platform_staff ? (
          <StaffDirectory onChoose={choose} />
        ) : (
          <ul className="cc-club-list">
            {(me.clubs as ClubProfile[]).map((club) => (
              <ClubOption key={club.slug} club={club} onChoose={choose} />
            ))}
          </ul>
        )}
        <Button variant="ghost" onClick={() => void signOut()}>
          {t("auth.signOut")}
        </Button>
      </main>
    </div>
  );
}

export function NoClubPage() {
  const t = useT();
  const { signOut } = useAuth();
  useDocumentTitle(t("club.noAccessTitle"));
  return (
    <div className="cc-center">
      <main className="cc-auth-card">
        <EmptyState icon="shield" title={t("club.noAccessTitle")} action={<Button onClick={() => void signOut()}>{t("auth.signOut")}</Button>}>
          {t("club.noAccessBody")}
        </EmptyState>
      </main>
    </div>
  );
}
